import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useUIStore } from '@stores/v4/useUIStore';
import { 
    calculateProgress,
    findFirstIncomplete,
    calculateNextStep,
    injectAdHocBlock,
    SystemStep 
} from '@shared/logic/performance-logic';
import { ActiveSession, PerformanceLog } from '@/types/app.types';
import { SessionOrchestrator } from '../../services/session-orchestrator';

interface SessionState {
    activeSession: ActiveSession | null;
    expandedBlockId: string | null;
    activeFocusId: string | null;
    systemStep: SystemStep | null;
    retroactiveDate: string | null;
}

interface SessionActions {
    getSessionProgress: () => { totalTarget: number; totalLogged: number; percent: number };
    getPerformanceLabel: () => string;
    startSession: (dayId: string | null, customDate?: string | null, sourceEventId?: string | null, isManual?: boolean, customEndDate?: string | null, label?: string) => Promise<void>;
    finishSession: () => Promise<void>;
    addLogEntry: (itemId: string, blockId: string, data: Partial<PerformanceLog>, isCircuit: boolean) => void;
    updateLogEntry: (itemId: string, logId: string | number, field: keyof PerformanceLog, value: any) => void;
    removeLogEntry: (itemId: string, logId: string | number) => void;
    toggleFocus: (itemId: string | null, blockId: string | null) => void;
    syncFocus: () => void;
    setExpandedBlock: (id: string | null) => void;
    setActiveFocus: (id: string | null) => void;
    addAdHocExercise: (exercise: any) => void;
    resetSession: () => void;
}

export const useSessionStore = create<SessionState & SessionActions>()(
    persist(
        (set, get) => ({
            activeSession: null,
            expandedBlockId: null,
            activeFocusId: null,
            systemStep: { blockId: null, itemId: null, round: 1 },
            retroactiveDate: null,

            getSessionProgress: () => calculateProgress(get().activeSession),

            getPerformanceLabel: () => {
                const session = get().activeSession;
                if (!session) return 'Activity';
                const label = session.label || 'Activity';
                return label.replace(/\s*(Session|Performance)$/i, '').trim();
            },

            startSession: async (dayId, customDate = null, sourceEventId = null, isManual = false, customEndDate = null, label = undefined) => {
                useUIStore.getState().setIsLoading(true);
                useUIStore.getState().setIsSessionMinimized(false);
                try {
                    const { session, retroDate } = await SessionOrchestrator.startSession(dayId, customDate, sourceEventId, isManual, customEndDate, label);
                    const { blockId, itemId } = findFirstIncomplete(session);

                    set({
                        activeSession: session,
                        expandedBlockId: blockId,
                        activeFocusId: itemId,
                        systemStep: { blockId, itemId, round: 1 },
                        retroactiveDate: retroDate
                    });
                } catch (err) {
                    console.error("[SessionStore] startSession FAILED:", err);
                } finally {
                    useUIStore.getState().setIsLoading(false);
                }
            },

            finishSession: async () => {
                const session = get().activeSession;
                if (!session) return;

                useUIStore.getState().setIsLoading(true);
                try {
                    await SessionOrchestrator.finishSession(session, get().retroactiveDate);
                    set({ activeSession: null, retroactiveDate: null, systemStep: null, activeFocusId: null, expandedBlockId: null });
                } catch (err: any) {
                    console.error("[SessionStore] finishSession FAILED:", err);
                    alert("Sync FAILED: " + (err.message || "Unknown Database Error"));
                } finally {
                    useUIStore.getState().setIsLoading(false);
                }
            },

            addLogEntry: (itemId, blockId, data, isCircuit) => {
                const session = get().activeSession;
                if (!session) return;

                const updatedLogs = { 
                    ...session.logs, 
                    [itemId]: [...(session.logs[itemId] || []), { ...data, id: String(Date.now()) }] 
                };
                
                const currentStep = get().systemStep;
                const next = calculateNextStep(
                    session,
                    blockId,
                    itemId,
                    currentStep?.round || 1,
                    isCircuit,
                    updatedLogs
                );

                const newState: Partial<SessionState & SessionActions> = { activeSession: { ...session, logs: updatedLogs } };
                if (next) {
                    newState.systemStep = next;
                    newState.activeFocusId = next.itemId;
                    newState.expandedBlockId = next.blockId;
                }
                
                set(newState as any);
            },

            updateLogEntry: (itemId, logId, field, value) => {
                const session = get().activeSession;
                if (!session) return;
                const updatedLogs = { ...session.logs, [itemId]: (session.logs[itemId] || []).map(l => l.id === logId ? { ...l, [field]: value } : l) };
                set({ activeSession: { ...session, logs: updatedLogs } });
            },

            removeLogEntry: (itemId, logId) => {
                const session = get().activeSession;
                if (!session) return;

                const updatedLogs = {
                    ...session.logs,
                    [itemId]: (session.logs[itemId] || []).filter(l => l.id !== logId)
                };
                set({ activeSession: { ...session, logs: updatedLogs } });
            },

            toggleFocus: (itemId, blockId) => set((state) => {
                const isClosing = state.activeFocusId === itemId;
                const newState: any = { activeFocusId: isClosing ? null : itemId, expandedBlockId: blockId };
                
                if (!isClosing && blockId && blockId !== state.systemStep?.blockId && state.activeSession) {
                    const block = state.activeSession.blocks.find(b => b.id === blockId);
                    if (block) {
                        const firstIncomplete = block.items.find(item => {
                            const logs = state.activeSession!.logs[item.id] || [];
                            const target = typeof item.target_sets === 'string' ? parseInt(item.target_sets) : (item.target_sets || 3);
                            return logs.length < target;
                        }) || block.items[0];
                        newState.systemStep = { blockId, itemId: firstIncomplete.id, round: state.systemStep?.round || 1 };
                    }
                }
                return newState;
            }),

            syncFocus: () => {
                const session = get().activeSession;
                if (!session) return;

                const { blockId, itemId } = findFirstIncomplete(session);
                set({ expandedBlockId: blockId, activeFocusId: itemId });
            },

            setExpandedBlock: (id) => set({ expandedBlockId: id }),
            setActiveFocus: (id) => set({ activeFocusId: id }),
            
            addAdHocExercise: (exercise: any) => {
                const session = get().activeSession;
                if (!session) return;
                const updatedSession = injectAdHocBlock(session, exercise);
                set({ activeSession: updatedSession });
            },

            resetSession: () => set({ activeSession: null, retroactiveDate: null, systemStep: null, activeFocusId: null, expandedBlockId: null })
        }),
        { 
            name: 'mp-active-session',
            partialize: (state) => ({ 
                activeSession: state.activeSession,
                expandedBlockId: state.expandedBlockId,
                activeFocusId: state.activeFocusId,
                systemStep: state.systemStep
            })
        }
    )
);

// We expose the store globally to allow lazy circular access if needed
if (typeof window !== 'undefined') {
    (window as any).useSessionStore = useSessionStore;
}
